import { useDocumentTitle } from "../../../use-document-title";
import { QaAutomationCharts } from "./charts";
import { QaAutomationHeaderAndStats } from "./header-and-stats";
import styles from "./index.module.css";
import { QaAutomationRecentRuns } from "./recent-runs";

/**
 * `/admin/qa-automation` — QA automation dashboard.
 *
 * Composes the three sibling slices built for this page into one route:
 * - `QaAutomationHeaderAndStats` — date-range tabs, "Start new run", the
 *   next-scheduled-run / runs-in-progress info cards, and the four KPI cards.
 * - `QaAutomationCharts` — "Verdict trend" and "Average run duration".
 * - `QaAutomationRecentRuns` — "5 slowest tests" / "Tests with the highest
 *   failure rate" ranked cards, plus the "Recent runs" table + pagination.
 *
 * Wrapped in the same page-level container/spacing convention as
 * `../../home.tsx` / `../../stub-page.tsx` (32px page padding). Unlike
 * those, this page has real custom content, so it doesn't use
 * `<StubPage>` — it calls `useDocumentTitle` directly instead. There is no
 * on-page `<h1>` here: the app shell's breadcrumb trail already reads
 * "QA Automation" for this route, so a duplicate page-level heading would
 * be redundant. `QaAutomationHeaderAndStats` also publishes the date-range
 * tab bar + "Start new run" action into the shell's secondary-header slot
 * rather than rendering them in the page body.
 *
 * No nested scroll box here — `<AppShell>`'s own `<main>` outlet (already
 * `overflow: auto`) scrolls the whole composed dashboard as one normal
 * page, same as every other route in this app. An internal `.pageScroll` +
 * `<ScrollShadow>` was tried here first, but that was solving a problem
 * nobody asked for: it added a second, nested scroll region with its own
 * "mid-scroll crop" behavior, when all that was ever needed was for
 * `.page` to be allowed to grow taller than the viewport (see
 * `index.module.css`'s own doc comment on `.page`) so `<main>` reaches its
 * real bottom padding by scrolling the actual page, not a box inside it.
 */
export function AdminQaAutomationPage() {
  useDocumentTitle("QA Automation");

  return (
    <div className={styles.page}>
      <QaAutomationHeaderAndStats />
      <QaAutomationCharts />
      <QaAutomationRecentRuns />
    </div>
  );
}
